import { useEffect, useState } from 'react';
import type { Liff } from '@line/liff';

declare global {
  interface Window {
    liff: Liff;
  }
}

interface LiffState {
  liff: Liff | null;
  isLoggedIn: boolean;
  isReady: boolean;
  error: Error | null;
  profile: {
    userId: string;
    displayName: string;
    pictureUrl?: string;
  } | null;
}

export function useLiff(liffId: string) {
  const [state, setState] = useState<LiffState>({
    liff: null,
    isLoggedIn: false,
    isReady: false,
    error: null,
    profile: null,
  });

  useEffect(() => {
    const initLiff = async () => {
      try {
        const liff = window.liff;

        await liff.init({ liffId });

        if (!liff.isLoggedIn()) {
          liff.login();
          return;
        }

        const profile = await liff.getProfile();

        setState({
          liff,
          isLoggedIn: true,
          isReady: true,
          error: null,
          profile: {
            userId: profile.userId,
            displayName: profile.displayName,
            pictureUrl: profile.pictureUrl,
          },
        });
      } catch (error) {
        console.error('LIFF initialization failed:', error);
        setState(prev => ({
          ...prev,
          error: error as Error,
          isReady: true,
        }));
      }
    };

    initLiff();
  }, [liffId]);

  const sendMessage = (text: string) => {
    if (state.liff && state.liff.isInClient()) {
      state.liff.sendMessages([{ type: 'text', text }]);
    }
  };

  const closeWindow = () => {
    if (state.liff && state.liff.isInClient()) {
      state.liff.closeWindow();
    }
  };

  const openWindow = (url: string, external = false) => {
    if (state.liff) {
      state.liff.openWindow({
        url,
        external,
      });
    }
  };

  return {
    ...state,
    sendMessage,
    closeWindow,
    openWindow,
  };
}
